import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useRef } from 'react';
import {
  askAgent,
  cancelJobWs,
  createAgentWebsocket,
  deleteChatHistory,
  editChatHistoryDescription,
  getChatHistories,
  reconnectToJob,
  sendToolApproval,
  submitAnswer,
} from '../../api/agents-client/agents-websockets';
import { ORGANIZATION_DOCUMENTS_QUERY_KEY } from '../../api/agents-client/agent-client.hooks';
import { formatToISODate } from '../../helpers/date-time-helpers/formatting/formatting';
import type {
  ArtifactSpec,
  AskUserQuestion,
  ChatHistory,
  FloraContextualInfo,
  Message,
  MessageSegment,
  SubagentInfo,
  TokenUsage,
  ToolApproval,
  ToolApprovalDecision,
  ToolCall,
} from './flora.type';
import {
  adoptStreamedToolCall,
  applyAutoModeBlock,
  appendToolInputChunk,
  finishToolInputStream,
  parseToolBlockedFrame,
  settleStreamingToolCalls,
  toAutoModeOutcome,
} from './flora-tool-input-streaming';
import { useFloraApprovalMode } from './components/flora-approval-mode-toggle/flora-approval-mode-toggle.hooks';
import { adoptFloraDraftApprovalMode, seedFloraChatApprovalModes } from '../../store/global-store/global-store.actions';
import { AutoModeOutcome, FloraVersion } from './flora.type';
import type { MemoryFileWriteFrame, MemoryWriteTracker } from './flora-memory-writes.helpers';
import { createMemoryWriteTracker, trackMemoryWriteFrame } from './flora-memory-writes.helpers';
import type { PendingPromptSubmit } from './flora-prompt-state.helpers';
import {
  applyReplayedAnswers,
  findPromptMessageIndex,
  isPromptSubmitAck,
  rollbackRejectedSubmit,
} from './flora-prompt-state.helpers';

/**
 * Retires any prompt still waiting on the user in the last message — a tool
 * approval or a clarifying-question card — once its turn is cancelled. The
 * cancelled job can no longer take an answer, so the card must stop accepting
 * input (it renders disabled without `pendingQuestions`/`pendingJobId`).
 *
 * @param {Message[]} messages - The conversation's assistant messages
 * @returns {Message[]} The same array when nothing was pending, else a copy with the prompts cleared
 */
const retirePendingPrompts = (messages: Message[]): Message[] => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage || !(lastMessage.pendingToolApproval || lastMessage.pendingQuestions || lastMessage.pendingJobId)) {
    return messages;
  }
  const updatedMessages = [...messages];
  updatedMessages[updatedMessages.length - 1] = {
    ...lastMessage,
    pendingToolApproval: undefined,
    pendingQuestions: undefined,
    pendingJobId: undefined,
    isLoading: false,
  };
  return updatedMessages;
};

/**
 * Ends the last message's turn as cancelled: settles any streaming tool call,
 * retires a pending prompt, clears the loading flag and marks a partial answer
 * with an "interrupted" divider (once).
 *
 * @param {Message[]} messages - The conversation's assistant messages
 * @returns {Message[]} A copy with the last message marked interrupted
 */
const markTurnInterrupted = (messages: Message[]): Message[] => {
  const updatedMessages = [...retirePendingPrompts(settleStreamingToolCalls(messages))];
  if (updatedMessages.length > 0) {
    const lastMessage = updatedMessages[updatedMessages.length - 1];
    const segments = lastMessage.segments ?? [];
    const hasInterrupted = segments.some((segment) => segment.type === 'interrupted');
    updatedMessages[updatedMessages.length - 1] = {
      ...lastMessage,
      isLoading: false,
      segments: segments.length > 0 && !hasInterrupted ? [...segments, { type: 'interrupted' }] : segments,
    };
  }
  return updatedMessages;
};

/**
 * Monotonic sequence backing {@link createToolCallId}. `Date.now()` alone
 * collides when two same-name tool calls start within the same millisecond,
 * which would make the grouped tool-call UI share expansion state and emit
 * duplicate React keys. The counter guarantees a unique id per call.
 */
let toolCallSequence = 0;

/**
 * Builds a collision-proof id for a streaming tool call. The id is used only for
 * React keys and per-call expansion state — tool completion is matched by name,
 * not id — so appending a monotonic suffix is safe.
 *
 * @param {string} toolName - The tool's name, kept as a readable id prefix
 * @returns {string} A unique id of the form `<toolName>-<timestamp>-<seq>`
 */
const createToolCallId = (toolName: string): string => `${toolName}-${Date.now()}-${toolCallSequence++}`;

/**
 * Appends a streaming text chunk to the last assistant message, extending the
 * trailing text segment when the parent_context matches or starting a new segment
 * otherwise. Extracted so the buffered (rAF-flushed) streaming path shares exact
 * append semantics with the original per-chunk reducer.
 *
 * @param {Message[]} prevMessages - Current messages array
 * @param {string} text - Non-empty accumulated text to append
 * @param {string | null} parentContext - Sub-agent/context tag used to group text segments
 * @param {string} turnId - The in-flight turn's id, stamped on a freshly-created message
 *   so it stays reachable by the turnId-keyed pairing lookup — see is_start's identical
 *   requirement in the caller's messageHandler
 * @returns {Message[]} New messages array with the text appended to the last message
 */
const appendStreamingText = (
  prevMessages: Message[],
  text: string,
  parentContext: string | null,
  turnId: string,
): Message[] => {
  const updatedMessages = [...prevMessages];
  if (updatedMessages.length === 0) {
    updatedMessages.push({
      content: text,
      role: 'assistant',
      timestamp: formatToISODate(new Date()) || '',
      segments: [{ type: 'text', content: text, parent_context: parentContext }],
      turnId,
    });
    return updatedMessages;
  }

  const lastMessage = updatedMessages[updatedMessages.length - 1];
  const segments = lastMessage.segments || [];
  const lastSegment = segments[segments.length - 1];

  let updatedSegments: MessageSegment[];
  if (lastSegment && lastSegment.type === 'text' && (lastSegment.parent_context || null) === parentContext) {
    updatedSegments = [
      ...segments.slice(0, -1),
      { type: 'text', content: lastSegment.content + text, parent_context: parentContext },
    ];
  } else {
    updatedSegments = [...segments, { type: 'text', content: text, parent_context: parentContext }];
  }

  updatedMessages[updatedMessages.length - 1] = {
    ...lastMessage,
    content: lastMessage.content + text,
    segments: updatedSegments,
  };
  return updatedMessages;
};

type FloraWsFrame = Record<string, unknown>;

/**
 * True only when a websocket frame carries streaming text and NOTHING else that
 * mutates message segments or finalizes the turn. Only such frames may be
 * buffered/coalesced; any frame that also carries a terminal or structural
 * payload must be committed synchronously so buffered text lands before that
 * payload's segment/finalization, regardless of the backend frame contract.
 *
 * @param {FloraWsFrame} data - Parsed websocket frame
 * @returns {boolean} Whether the frame is a pure, safely-bufferable text chunk
 */
const isPureTextFrame = (data: FloraWsFrame): boolean => {
  if (!data.is_update || typeof data.text !== 'string' || data.text.length === 0) {
    return false;
  }
  return !(
    data.is_start ||
    data.is_done ||
    data.type === 'error' ||
    data.type === 'stalled' ||
    data.type === 'job_cancelled' ||
    data.is_image ||
    data.is_document ||
    data.is_artifact ||
    data.is_tool_start ||
    data.is_tool_end ||
    data.is_tool_error ||
    data.is_tool_blocked ||
    data.is_tool_input_chunk ||
    data.is_tool_input_end ||
    data.is_subagent_start ||
    data.is_subagent_end ||
    data.is_suggested_followups ||
    data.is_ask_user_questions ||
    data.is_tool_approval_required ||
    data.is_compacting ||
    data.is_compacting_finished
  );
};

/**
 * Coerce a websocket frame's `subagent_id` to a usable identity. Only a
 * non-empty string is a real per-instance id; anything else (absent, null,
 * empty, non-string) becomes `undefined` so grouping and end-event removal fall
 * back to the subagent name (older persisted chats carry no id) instead of
 * matching on a bogus value.
 *
 * @param {unknown} value - The frame's raw `subagent_id`.
 * @returns {string | undefined} The id when it is a non-empty string.
 */
const normalizeSubagentId = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

/**
 * Custom hook for managing WebSocket connections and chat interactions with an AI agent
 * @param {string | null} orgId - Organization ID for the current user
 * @param {boolean} useAI - Boolean flag indicating if AI features are enabled
 * @param {string} threadId - Current chat thread ID
 * @param {FloraVersion} floraVersion - Current flora version
 * @param {string | null} portfolioId - Current portfolio ID for context
 * @param {FloraContextualInfo | null} contextualInfo - Current contextual information for the user
 * @param {(id: string) => void} setThreadId - Function to update the thread ID
 * @param {Dispatch<SetStateAction<ChatHistory[]>>} setChatHistories - Function to update the chat history list
 * @param {Dispatch<SetStateAction<boolean>>} setIsActiveQuestion - Function to toggle the active question state
 * @param {Dispatch<SetStateAction<boolean>>} setIsWaitingForFirstResponse - Function to toggle waiting for first response state
 * @param {Dispatch<SetStateAction<Message[]>>} setMessages - Function to update the messages array
 * @param {Dispatch<SetStateAction<string>>} setLoadingMessage - Function to update the loading message text
 * @param {() => void} handleNewChat - Function to reset chat state for a new conversation
 * @param {Dispatch<SetStateAction<TokenUsage | null>>} setTokenUsage - Optional function to update token usage state
 * @param {boolean} enableArtifacts - Whether to enable artifact generation and suggested followups (default: false)
 * @param {string} floraModel - Optional super-user-selected model id forwarded to the agent
 * @param {string | null} floraEffort - Optional super-user-selected reasoning effort forwarded to the agent
 * @param {boolean} isSidebar - Whether the request comes from the compact docked side panel, so the
 *   backend can disclose that surface's limitations (chart artifacts do not render there)
 * @returns {Object} Object containing:
 *   - runAgent: Function to initiate a chat with the AI agent by sending a question
 *   - fetchChatHistories: Function to retrieve the list of previous chat histories
 *   - handleDeleteHistory: Function to delete a specific chat history thread
 *   - handleEditHistory: Function to edit the description of a chat history thread
 */

const useWebsocketHandlers = (
  orgId: string | null,
  useAI: boolean,
  threadId: string,
  floraVersion: FloraVersion,
  _portfolioId: string | null,
  _contextualInfo: FloraContextualInfo | null,
  setThreadId: (id: string) => void,
  setChatHistories: Dispatch<SetStateAction<ChatHistory[]>>,
  setIsActiveQuestion: Dispatch<SetStateAction<boolean>>,
  setIsWaitingForFirstResponse: Dispatch<SetStateAction<boolean>>,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  setLoadingMessage: Dispatch<SetStateAction<string>>,
  handleNewChat: () => void,
  setTokenUsage?: Dispatch<SetStateAction<TokenUsage | null>>,
  enableArtifacts?: boolean,
  floraModel?: string,
  floraEffort?: string | null,
  isSidebar?: boolean,
  documentIds?: string[],
) => {
  const queryClient = useQueryClient();
  // Read here rather than threaded through by each caller so every Flora
  // surface (full page and side panel) sends the same Ask/Auto choice for the
  // same chat. `floraApprovalMode` is the resolved mode the composer toggle
  // shows (chat choice, else saved default, else Ask), so the label never
  // differs from what runs; `floraChatApprovalMode` is the chat's own choice,
  // which the backend saves on the chat. Without the org's Auto mode flag both
  // are null and nothing is sent; the backend also forces Ask in that case.
  const approval = useFloraApprovalMode(threadId);
  const floraApprovalMode = approval?.mode ?? null;
  const floraChatApprovalMode = approval?.chatMode ?? null;

  // Store a ref to the active WebSocket so handleAnswerSubmit can use it
  const activeWebSocketRef = useRef<WebSocket | null>(null);
  // Track the active job ID for stop/reconnect
  const activeJobIdRef = useRef<string | null>(null);
  // Set by `stopAgent`, cleared whenever a turn is deliberately started or
  // resumed. Guards against frames that were already in flight when the user
  // stopped — chiefly a `reconnect_status` describing the cancelled job.
  const userStoppedRef = useRef(false);
  // Track submitted tool approval jobIds to prevent duplicate sends on rapid clicks
  const submittedApprovalJobIdsRef = useRef<Set<string>>(new Set());
  // The answer or approval sent and not yet acknowledged — see settlePromptSubmit.
  const pendingSubmitRef = useRef<PendingPromptSubmit | null>(null);
  // Which kind of prompt the active job is parked on, so an answer only ever
  // resumes a question and a decision only ever resumes an approval — the
  // backend claims any waiting job whatever the resume message's type.
  const parkedPromptRef = useRef<{ jobId: string; kind: PendingPromptSubmit['kind'] } | null>(null);
  // Track the currently-viewed thread to guard stale websocket updates
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  // Buffer streaming text chunks and flush them once per animation frame (see the
  // is_update branches in runAgent/reconnect) so a long response doesn't re-render
  // and re-parse the growing message's markdown on every single websocket frame.
  const pendingTextRef = useRef('');
  const pendingParentContextRef = useRef<string | null>(null);
  // The in-flight turn's id, set by runAgent/reconnectAgent before any frame can
  // arrive. commitPendingText can fire with an empty `messages` array (e.g. a
  // history refetch clears it mid-replay) and must stamp the freshly-created
  // message with this id — otherwise it is unreachable by the turnId-keyed
  // pairing lookup and the whole response renders nowhere.
  const pendingTurnIdRef = useRef('');
  const rafIdRef = useRef<number | null>(null);
  const commitPendingText = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    const text = pendingTextRef.current;
    if (text.length === 0) {
      return;
    }
    const parentContext = pendingParentContextRef.current;
    const turnId = pendingTurnIdRef.current;
    pendingTextRef.current = '';
    setMessages((prevMessages) => appendStreamingText(prevMessages, text, parentContext, turnId));
  }, [setMessages]);

  const ACTIVE_JOB_KEY = 'flora-active-job';
  // Generic message shown for any failed run — a {type:"error"} event or a hard
  // socket drop. We deliberately don't surface the raw backend error to the user.
  const FLORA_ERROR_FALLBACK_MESSAGE = 'Flora ran into an error and couldn’t finish responding. Please try again.';
  const invalidateSlashCommands = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['slash-commands'] });
  }, [queryClient.invalidateQueries]);
  // refetchType 'all' is load-bearing: the app sets refetchOnMount: false
  // globally, so a query invalidated while its screen is unmounted (e.g.
  // Customize Flora while the user approves in chat) would otherwise never
  // refetch — mount short-circuits before the invalidated flag is consulted.
  const invalidateMemories = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['flora-memories'], refetchType: 'all' });
  }, [queryClient.invalidateQueries]);
  const invalidateUserPreferences = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['flora-user-preferences'], refetchType: 'all' });
  }, [queryClient.invalidateQueries]);
  // A Flora document was created or edited this turn, so the cached Documents
  // list (shared by the page and the nav-visibility check) is out of date.
  const invalidateOrganizationDocuments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ORGANIZATION_DOCUMENTS_QUERY_KEY], refetchType: 'all' });
  }, [queryClient.invalidateQueries]);
  // Runs before a handler's stale-thread early return: a write still lands
  // server-side after the user switches threads, so its cache must refresh.
  // The tracker is per socket (see runAgent/reconnectAgent), so an abandoned
  // run's unsettled calls are dropped with it.
  const trackMemoryFileWrite = useCallback(
    (tracker: MemoryWriteTracker, data: MemoryFileWriteFrame) => {
      for (const kind of trackMemoryWriteFrame(tracker, data)) {
        if (kind === 'preferences') {
          invalidateUserPreferences();
        } else {
          invalidateMemories();
        }
      }
    },
    [invalidateMemories, invalidateUserPreferences],
  );
  /**
   * Settles an answer or approval awaiting the backend's ack against a frame
   * from the socket it was sent on. Every refusal of a submit (another tab
   * answered first, the job moved on, a retryable enqueue failure) arrives as a
   * plain `{type:"error"}`, which would otherwise end the run: while a submit is
   * pending, that error rolls the prompt back instead — the card is answerable
   * again and shows the reason, and the socket stays open for the retry.
   *
   * @param {Record<string, unknown>} data - The parsed socket frame
   * @param {WebSocket} socket - The socket the frame arrived on
   * @param {boolean} isStaleThread - Whether the user has left the frame's thread
   * @returns {boolean} True when the frame was a refusal and has been handled
   */
  const settlePromptSubmit = useCallback(
    (data: Record<string, unknown>, socket: WebSocket, isStaleThread: boolean): boolean => {
      const pending = pendingSubmitRef.current;
      if (!pending || pending.socket !== socket) return false;
      if (data.type !== 'error') {
        if (isPromptSubmitAck(data, pending)) pendingSubmitRef.current = null;
        return false;
      }
      pendingSubmitRef.current = null;
      // The user has moved to another thread; there is no card on screen to
      // re-arm, so let the error end this socket as usual.
      if (isStaleThread) return false;
      if (pending.kind === 'approval') {
        submittedApprovalJobIdsRef.current.delete(pending.jobId);
      }
      parkedPromptRef.current = { jobId: pending.jobId, kind: pending.kind };
      // Parked again — the history's active_job_id owns any reload.
      try {
        localStorage.removeItem(ACTIVE_JOB_KEY);
      } catch {
        /* storage unavailable */
      }
      const reason = typeof data.message === 'string' ? data.message : '';
      setMessages((prevMessages: Message[]) => rollbackRejectedSubmit(prevMessages, pending, reason));
      return true;
    },
    [setMessages],
  );
  const invalidateCapabilities = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['flora-capabilities'], refetchType: 'all' });
  }, [queryClient.invalidateQueries]);

  /**
   * Initiates a chat interaction with the AI agent by sending a question
   * @param {string} question - The question or message to send to the AI agent
   * @param {string} turnId - Stable id (generated by the caller with `createTurnId`)
   *   identifying this turn, stamped on every assistant `Message` this closure
   *   creates so the answer pairs with its question by id instead of by array position
   * @returns {Function} Cleanup function to remove event listeners and close websocket connection
   */
  const runAgent = useCallback(
    (question: string, turnId: string) => {
      if (orgId && useAI) {
        // A new turn is deliberate, so the previous Stop no longer applies.
        userStoppedRef.current = false;
        // If a prior turn is still in-flight (e.g. user rejected an HITL tool
        // prompt and the agent is still streaming the rejection-aftermath),
        // close its socket so backend channels stops delivering events to it.
        // We deliberately do NOT send cancel_job here — revoke(terminate=True)
        // SIGTERMs the celery worker mid-stream, which can leave the LangGraph
        // checkpointer's state for this thread_id in an inconsistent shape and
        // cause the next turn (on the same thread) to hang. Letting the old
        // task complete naturally is fine; the stale-WS guard below + the
        // closed socket together prevent any leftover events from polluting
        // the new turn's message slot.
        const previousWebSocket = activeWebSocketRef.current;
        if (previousWebSocket) {
          previousWebSocket.close();
        }
        // New turn: flush any leftover buffered text from the prior turn to its
        // message and cancel its pending rAF, so a stale flush can't wipe this
        // turn's buffer and old text can't bleed into the new message.
        commitPendingText();

        const agentWebsocket = createAgentWebsocket(orgId as string, floraVersion);
        activeWebSocketRef.current = agentWebsocket;
        activeJobIdRef.current = null;
        const memoryFileWrites = createMemoryWriteTracker();
        // Set after flushing the prior turn's buffer above, before this turn's
        // socket can deliver any frame — see appendStreamingText's turnId param.
        pendingTurnIdRef.current = turnId;
        setIsActiveQuestion(true);
        setIsWaitingForFirstResponse(true);

        // Capture the thread this websocket was opened for (mutable — updated when a new thread is assigned)
        let originThreadId = threadIdRef.current;

        const messageHandler = (event: MessageEvent) => {
          const data = JSON.parse(event.data);

          // Drop events from a superseded websocket. runAgent() reassigns
          // activeWebSocketRef when the user starts a new turn (e.g. after
          // rejecting an HITL tool prompt) — without this guard, the old
          // turn's lingering is_update chunks would mutate the new turn's
          // last message. Mirrors the matching guards further down this file.
          if (activeWebSocketRef.current !== agentWebsocket) {
            agentWebsocket.close();
            return;
          }

          // Check if user has navigated to a different thread since this websocket was opened
          const isStaleThread = threadIdRef.current !== originThreadId;
          trackMemoryFileWrite(memoryFileWrites, data);
          // Before any stale-thread return: the document was still created
          // server-side, so the cached Documents list must refresh even when
          // the user has switched to another chat.
          if (data.is_document && data.document_data) invalidateOrganizationDocuments();
          if (settlePromptSubmit(data, agentWebsocket, isStaleThread)) return;

          // Commit any buffered streaming text before handling a non-text frame so it
          // lands in order ahead of tool/artifact/terminal segments. Text chunks skip
          // this and accumulate in the buffer (flushed on rAF, see is_update below).
          if (isStaleThread) {
            pendingTextRef.current = '';
          } else if (!isPureTextFrame(data)) {
            commitPendingText();
          }

          // Handle loading message updates
          if (data.loading_message && !isStaleThread) {
            setLoadingMessage(data.loading_message);
          }

          // Handle job_created — store job ID for stop/reconnect (always runs for cleanup)
          if (data.type === 'job_created' && data.job_id) {
            activeJobIdRef.current = data.job_id;
            if (!threadId && data.thread_id) {
              setThreadId(data.thread_id);
              // Carry a mode switched on the new chat onto the chat just created.
              adoptFloraDraftApprovalMode(data.thread_id);
              // Update originThreadId so subsequent messages aren't treated as stale
              originThreadId = data.thread_id;
            }
            try {
              localStorage.setItem(
                ACTIVE_JOB_KEY,
                JSON.stringify({ jobId: data.job_id, threadId: data.thread_id, orgId }),
              );
            } catch {
              /* storage unavailable */
            }
          }

          // Handle live chat title updates — runs regardless of stale-thread state so
          // the sidebar reflects the generated title even when the user navigated away.
          // Also broadcast a window event so sibling panels (e.g. the /flora chat history
          // side panel) that don't share state with this hook can update too.
          if (data.type === 'chat_description_updated' && data.thread_id) {
            setChatHistories((prev) =>
              prev.map((history) =>
                history.id === data.thread_id ? { ...history, description: data.description } : history,
              ),
            );
            window.dispatchEvent(
              new CustomEvent('flora:chat-description-updated', {
                detail: { thread_id: data.thread_id, description: data.description },
              }),
            );
          }

          // Handle job_cancelled — cleanup always runs, but skip UI updates if stale
          if (data.type === 'job_cancelled' || (data.is_done && data.type === 'job_cancelled')) {
            if (activeWebSocketRef.current === agentWebsocket) {
              if (!isStaleThread) {
                setIsWaitingForFirstResponse(false);
                setIsActiveQuestion(false);
                setLoadingMessage('Flora is thinking...');
                // Retire any pending approval or question card and mark the
                // partial response with an interrupted divider
                setMessages(markTurnInterrupted);
              }
              activeJobIdRef.current = null;
              activeWebSocketRef.current = null;
              try {
                localStorage.removeItem(ACTIVE_JOB_KEY);
              } catch {
                /* storage unavailable */
              }
            }
            agentWebsocket.close();
          }

          // Skip all remaining message/UI updates if the thread is stale
          if (isStaleThread) {
            // Still allow is_done to clean up below
            if (data.is_done) {
              if (activeWebSocketRef.current === agentWebsocket) {
                activeJobIdRef.current = null;
                try {
                  localStorage.removeItem(ACTIVE_JOB_KEY);
                } catch {
                  /* storage unavailable */
                }
                activeWebSocketRef.current = null;
              }
              agentWebsocket.close();
            }
            return;
          }

          if (data.is_start) {
            setIsWaitingForFirstResponse(true);
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];

              // If the last message already belongs to this turn (waiting for
              // resume after answer submit), continue appending to it instead
              // of pushing a new message.
              const lastMessage = updatedMessages[updatedMessages.length - 1] || '';
              if (lastMessage?.turnId === turnId && lastMessage.role === 'assistant') {
                return updatedMessages;
              }

              const initialSegments: MessageSegment[] =
                data.text && data.text.length > 0 ? [{ type: 'text', content: data.text }] : [];
              updatedMessages.push({
                content: data.text || '',
                role: 'assistant',
                timestamp: formatToISODate(new Date()) || '',
                segments: initialSegments,
                turnId,
              });
              return updatedMessages;
            });
          }

          if (data.is_update) {
            if (data.text && data.text.length > 0) {
              setIsWaitingForFirstResponse(false);
            }
            if (!threadId && data.thread_id) {
              setThreadId(data.thread_id);
              adoptFloraDraftApprovalMode(data.thread_id);
              originThreadId = data.thread_id;
            }
            // Buffer streaming text and flush on the next animation frame rather than
            // re-rendering (and re-parsing the growing message's markdown) on every
            // chunk. Non-text frames flush the buffer synchronously first (top of this
            // handler), so segment ordering with tool/artifact/terminal frames holds.
            if (data.text && data.text.length > 0) {
              const parentContext = data.parent_context || null;
              if (pendingTextRef.current.length > 0 && pendingParentContextRef.current !== parentContext) {
                commitPendingText();
              }
              pendingTextRef.current += data.text;
              pendingParentContextRef.current = parentContext;
              if (!isPureTextFrame(data)) {
                // Frame also carries a terminal/structural payload — commit now so the
                // text lands before that payload is handled in the branches below.
                commitPendingText();
              } else if (rafIdRef.current === null) {
                rafIdRef.current = requestAnimationFrame(() => {
                  rafIdRef.current = null;
                  if (activeWebSocketRef.current !== agentWebsocket || threadIdRef.current !== originThreadId) {
                    pendingTextRef.current = '';
                    return;
                  }
                  commitPendingText();
                });
              }
            }
          }
          if (data.is_done) {
            // Only reset shared UI state if this socket is still the active one
            // (avoids clearing state for a newer run that may have started)
            if (activeWebSocketRef.current === agentWebsocket) {
              setIsWaitingForFirstResponse(false);
              setIsActiveQuestion(false);
              // Reset loading message to default when done
              setLoadingMessage('Flora is thinking...');
              // Clear active job tracking
              activeJobIdRef.current = null;
              try {
                localStorage.removeItem(ACTIVE_JOB_KEY);
              } catch {
                /* storage unavailable */
              }
              // Clear isLoading and any pending tool approvals on the last message
              setMessages((prevMessages: Message[]) => {
                const updatedMessages = [...settleStreamingToolCalls(prevMessages)];
                if (updatedMessages.length > 0) {
                  const lastMessage = updatedMessages[updatedMessages.length - 1];
                  if (lastMessage.isLoading || lastMessage.pendingToolApproval) {
                    updatedMessages[updatedMessages.length - 1] = {
                      ...lastMessage,
                      isLoading: false,
                      pendingToolApproval: undefined,
                    };
                  }
                }
                return updatedMessages;
              });
              activeWebSocketRef.current = null;
            }
            agentWebsocket.close();
          }

          // Handle terminal error — the backend emits {type:"error"} when a Flora
          // run fails mid-stream (no is_done follows). Without this branch the
          // spinner state is never cleared and the UI spins forever.
          if (data.type === 'error') {
            if (activeWebSocketRef.current === agentWebsocket) {
              if (!isStaleThread) {
                setIsWaitingForFirstResponse(false);
                setIsActiveQuestion(false);
                setLoadingMessage('Flora is thinking...');
                setMessages((prevMessages: Message[]) => {
                  // The run is over, so no prompt on it can be answered any more.
                  const updatedMessages = [...retirePendingPrompts(settleStreamingToolCalls(prevMessages))];
                  const errorSegment: MessageSegment = {
                    type: 'error',
                    message: FLORA_ERROR_FALLBACK_MESSAGE,
                  };
                  const lastMessage = updatedMessages[updatedMessages.length - 1];
                  // Only mutate the last message when it's this turn's own assistant
                  // slot — otherwise (e.g. the run errored before is_start ever
                  // pushed one) push a fresh, turnId-stamped error bubble instead of
                  // mutating a message that belongs to a different turn.
                  if (lastMessage && lastMessage.role === 'assistant' && lastMessage.turnId === turnId) {
                    updatedMessages[updatedMessages.length - 1] = {
                      ...lastMessage,
                      isLoading: false,
                      pendingToolApproval: undefined,
                      segments: [...(lastMessage.segments ?? []), errorSegment],
                    };
                  } else {
                    updatedMessages.push({
                      content: '',
                      role: 'assistant',
                      timestamp: formatToISODate(new Date()) || '',
                      segments: [errorSegment],
                      turnId,
                    });
                  }
                  return updatedMessages;
                });
              }
              activeJobIdRef.current = null;
              try {
                localStorage.removeItem(ACTIVE_JOB_KEY);
              } catch {
                /* storage unavailable */
              }
              activeWebSocketRef.current = null;
            }
            agentWebsocket.close();
            return;
          }

          // handle image codes
          if (data.is_image) {
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  images: [...(lastMessage.images || []), data.image_data],
                };
              }
              return updatedMessages;
            });
          }

          // Handle document attachments
          if (data.is_document && data.document_data) {
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                const newDocs = Array.isArray(data.document_data) ? data.document_data : [data.document_data];
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  documents: [...(lastMessage.documents || []), ...newDocs],
                };
              }
              return updatedMessages;
            });
          }

          // Handle artifact messages
          if (data.is_artifact && data.artifact_data) {
            const artifactData = data.artifact_data as ArtifactSpec;
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                const segments = lastMessage.segments || [];
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  artifacts: [...(lastMessage.artifacts || []), artifactData],
                  segments: [...segments, { type: 'artifact', artifact: artifactData }],
                };
              }
              return updatedMessages;
            });
          }

          // Tool arguments streamed while the model is still writing them
          // (LangChain agent). Large inputs such as Pages render progressively
          // before the tool runs; the authoritative input follows on tool_start.
          if (data.is_tool_input_chunk && typeof data.tool_name === 'string') {
            // A turn can go straight to a tool with no preamble text (e.g. a
            // page publish). A text chunk is the usual signal that clears the
            // "waiting for first response" state; clear it here too, or isLive
            // stays false and the live "Writing…" tool UI is suppressed for the
            // whole turn.
            setIsWaitingForFirstResponse(false);
            const toolName = data.tool_name;
            setMessages((prevMessages: Message[]) =>
              appendToolInputChunk(
                prevMessages,
                {
                  tool_name: toolName,
                  tool_call_id: typeof data.tool_call_id === 'string' ? data.tool_call_id : null,
                  args: typeof data.args === 'string' ? data.args : '',
                },
                () => createToolCallId(toolName),
              ),
            );
          }

          if (data.is_tool_input_end && typeof data.tool_name === 'string') {
            setMessages((prevMessages: Message[]) =>
              finishToolInputStream(prevMessages, {
                tool_name: data.tool_name as string,
                tool_call_id: typeof data.tool_call_id === 'string' ? data.tool_call_id : null,
                tool_input: (data.tool_input as Record<string, unknown> | null | undefined) ?? null,
              }),
            );
          }

          // Handle tool call start (LangChain agent)
          if (data.is_tool_start) {
            // Same as tool_input_chunk: a tool-first turn (no preamble text and
            // no streamed args, e.g. a non-eager provider) must still count as
            // live so the tool renders as running rather than sitting behind
            // the global "thinking" spinner.
            setIsWaitingForFirstResponse(false);
            // tool_start only ever carries `approved`; anything else is dropped so a
            // running call can never show a "blocked" tag.
            const autoMode =
              toAutoModeOutcome(data.auto_mode) === AutoModeOutcome.Approved ? AutoModeOutcome.Approved : undefined;
            const newToolCall: ToolCall = {
              id: createToolCallId(data.tool_name),
              tool_name: data.tool_name,
              tool_input: data.tool_input || {},
              status: 'pending',
              parent_context: data.parent_context || null,
              ...(autoMode ? { auto_mode: autoMode } : {}),
            };
            setMessages((prevMessages: Message[]) => {
              // A call whose arguments were streamed already has a segment —
              // promote it to pending instead of appending a duplicate.
              const adopted = adoptStreamedToolCall(prevMessages, {
                tool_name: data.tool_name,
                tool_input: data.tool_input || {},
                parent_context: data.parent_context || null,
                auto_mode: autoMode,
              });
              if (adopted) {
                return adopted;
              }
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                const segments = lastMessage.segments || [];
                // Add tool call as a segment to maintain order
                const newSegment: MessageSegment = {
                  type: 'tool_call',
                  toolCall: newToolCall,
                };
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  toolCalls: [...(lastMessage.toolCalls || []), newToolCall],
                  segments: [...segments, newSegment],
                };
              }
              return updatedMessages;
            });
          }

          // Handle tool call end (LangChain agent)
          if (data.is_tool_end) {
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];

                // Find the first pending tool call with matching name
                const pendingIndex = (lastMessage.toolCalls || []).findIndex(
                  (tc: ToolCall) => tc.tool_name === data.tool_name && tc.status === 'pending',
                );

                const updatedToolCalls = (lastMessage.toolCalls || []).map((tc: ToolCall, index: number) => {
                  // Only update the first pending tool call with matching name
                  if (index === pendingIndex) {
                    return {
                      ...tc,
                      tool_output:
                        typeof data.tool_output === 'string' ? data.tool_output : JSON.stringify(data.tool_output),
                      status: 'completed' as const,
                    };
                  }
                  return tc;
                });

                // Also update the corresponding tool call in segments
                // Track if we've already updated one to ensure we only update the first match
                let segmentUpdated = false;
                const updatedSegments = (lastMessage.segments || []).map((segment: MessageSegment) => {
                  if (
                    !segmentUpdated &&
                    segment.type === 'tool_call' &&
                    segment.toolCall.tool_name === data.tool_name &&
                    segment.toolCall.status === 'pending'
                  ) {
                    segmentUpdated = true;
                    return {
                      ...segment,
                      toolCall: {
                        ...segment.toolCall,
                        tool_output:
                          typeof data.tool_output === 'string' ? data.tool_output : JSON.stringify(data.tool_output),
                        status: 'completed' as const,
                      },
                    };
                  }
                  return segment;
                });

                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  toolCalls: updatedToolCalls,
                  segments: updatedSegments,
                };
              }
              return updatedMessages;
            });

            // Refresh caches when Flora writes via a tool. Match the BACKEND
            // tool names (see flora_langchain/tools/actions) — the stream's
            // tool_name is the tool's registered name. Memory and learned
            // preference changes are filesystem writes under /memories/,
            // matched by path when the call starts.
            if (data.tool_name === 'slash_command_upsert') {
              invalidateSlashCommands();
            }
          }

          // Handle tool call error (LangChain agent)
          if (data.is_tool_error) {
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];

                // Find the first pending tool call with matching name
                const pendingIndex = (lastMessage.toolCalls || []).findIndex(
                  (tc: ToolCall) => tc.tool_name === data.tool_name && tc.status === 'pending',
                );

                const updatedToolCalls = (lastMessage.toolCalls || []).map((tc: ToolCall, index: number) => {
                  // Only update the first pending tool call with matching name
                  if (index === pendingIndex) {
                    return {
                      ...tc,
                      tool_error: data.tool_error,
                      status: 'error' as const,
                    };
                  }
                  return tc;
                });

                // Also update the corresponding tool call in segments
                let segmentUpdated = false;
                const updatedSegments = (lastMessage.segments || []).map((segment: MessageSegment) => {
                  if (
                    !segmentUpdated &&
                    segment.type === 'tool_call' &&
                    segment.toolCall.tool_name === data.tool_name &&
                    segment.toolCall.status === 'pending'
                  ) {
                    segmentUpdated = true;
                    return {
                      ...segment,
                      toolCall: {
                        ...segment.toolCall,
                        tool_error: data.tool_error,
                        status: 'error' as const,
                      },
                    };
                  }
                  return segment;
                });

                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  toolCalls: updatedToolCalls,
                  segments: updatedSegments,
                };
              }
              return updatedMessages;
            });
          }

          // A call auto mode refused to run (or could not risk-check). It never
          // gets tool_start/tool_end, so this frame is its only signal.
          if (data.is_tool_blocked) {
            const blockedFrame = parseToolBlockedFrame(data);
            if (blockedFrame) {
              setIsWaitingForFirstResponse(false);
              setMessages((prevMessages: Message[]) =>
                applyAutoModeBlock(prevMessages, blockedFrame, () => createToolCallId(blockedFrame.tool_name)),
              );
            }
          }

          // Handle subagent start
          if (data.is_subagent_start && data.subagent_name) {
            const subagentInfo: SubagentInfo = {
              id: normalizeSubagentId(data.subagent_id),
              name: data.subagent_name,
              description: data.description,
              status: 'in_progress',
            };

            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  segments: [...(lastMessage.segments || []), { type: 'subagent_start', subagent: subagentInfo }],
                  activeSubagents: [...(lastMessage.activeSubagents || []), subagentInfo],
                };
              }
              return updatedMessages;
            });
          }

          // Handle subagent end
          if (data.is_subagent_end && data.subagent_name) {
            const subagentId = normalizeSubagentId(data.subagent_id);
            const subagentInfo: SubagentInfo = {
              id: subagentId,
              name: data.subagent_name,
              status: data.error ? 'error' : 'completed',
              output: data.output,
              error: data.error,
            };

            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];

                // Remove the finished instance. Match by id when present (the
                // LangChain agent runs same-named subagents in parallel); fall
                // back to name when the frame carries no id.
                const updatedActiveSubagents = (lastMessage.activeSubagents || []).filter((sa: SubagentInfo) =>
                  subagentId ? sa.id !== subagentId : sa.name !== data.subagent_name,
                );

                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  segments: [...(lastMessage.segments || []), { type: 'subagent_end', subagent: subagentInfo }],
                  activeSubagents: updatedActiveSubagents,
                };
              }
              return updatedMessages;
            });
          }

          // Handle suggested followups
          if (data.is_suggested_followups && data.suggested_followups) {
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  suggestedFollowups: data.suggested_followups,
                };
              }
              return updatedMessages;
            });
          }

          // Handle ask_user_questions interrupt
          if (data.is_ask_user_questions && data.questions) {
            const questions = data.questions as AskUserQuestion[];
            const jobId = data.job_id as string;
            parkedPromptRef.current = { jobId, kind: 'answer' };
            // Parked job: reloads are owned by the history's active_job_id, as
            // for a tool approval below. The key is re-set when an answer
            // resumes the run.
            try {
              localStorage.removeItem(ACTIVE_JOB_KEY);
            } catch {
              /* storage unavailable */
            }
            setIsWaitingForFirstResponse(false);
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                const segments = lastMessage.segments || [];
                // One interrupt at a time: a new question supersedes any
                // approval still in the other slot.
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  segments: [...segments, { type: 'ask_user_questions', questions }],
                  pendingQuestions: questions,
                  pendingJobId: jobId,
                  pendingToolApproval: undefined,
                  promptRejection: undefined,
                  isLoading: false,
                };
              }
              return updatedMessages;
            });
          }

          // Handle HITL tool approval request (Jira write tools)
          if (data.is_tool_approval_required) {
            const toolApproval: ToolApproval = {
              actionRequests: data.action_requests ?? [],
              reviewConfigs: data.review_configs ?? [],
              threadId: data.thread_id,
              jobId: data.job_id,
            };
            parkedPromptRef.current = data.job_id ? { jobId: data.job_id, kind: 'approval' } : null;
            if (data.job_id) {
              // A fresh interrupt re-arms the decision buttons — the
              // double-click dedupe only needs to hold until the next prompt.
              submittedApprovalJobIdsRef.current.delete(data.job_id);
            }
            // Parked job: reloads are owned by the history's active_job_id —
            // a mount-time reconnect from this key would block the history
            // load. The key is re-set when a decision resumes the run.
            try {
              localStorage.removeItem(ACTIVE_JOB_KEY);
            } catch {
              /* storage unavailable */
            }
            setIsWaitingForFirstResponse(false);
            setMessages((prevMessages: Message[]) => {
              // A tool awaiting approval already finished streaming its input
              // (on_chat_model_end always precedes the interrupt), but settle
              // defensively so a missed/reordered end event can't leave a
              // streaming spinner running through an indefinite HITL park.
              const updatedMessages = [...settleStreamingToolCalls(prevMessages)];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                const segments = lastMessage.segments || [];
                // One interrupt at a time: a new approval supersedes any
                // question still in the other slot.
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  segments: [...segments, { type: 'tool_approval', toolApproval }],
                  pendingToolApproval: toolApproval,
                  pendingQuestions: undefined,
                  pendingJobId: undefined,
                  promptRejection: undefined,
                  isLoading: false,
                };
              }
              return updatedMessages;
            });
          }

          // Handle token usage messages (Flora 3 / LangChain agent)
          if (data.is_token_usage && floraVersion === FloraVersion.LANGCHAIN && setTokenUsage) {
            setTokenUsage({
              context_used: data.context_used,
              context_limit: data.context_limit,
              percentage: data.percentage,
            });
          }

          // Handle compaction start — add inline placeholder segment
          if (data.is_compacting) {
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                const segments = lastMessage.segments || [];
                const lastSegment = segments[segments.length - 1];
                const hasUnfinishedCompactionPlaceholder =
                  lastSegment?.type === 'context_compacted' && lastSegment.finished === false;

                if (hasUnfinishedCompactionPlaceholder) {
                  return updatedMessages;
                }

                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  segments: [...segments, { type: 'context_compacted', percentage: 0, finished: false }],
                };
              }
              return updatedMessages;
            });
          }

          // Handle compaction finished — update the placeholder
          if (data.is_compacting_finished) {
            setMessages((prevMessages: Message[]) => {
              const updatedMessages = [...prevMessages];
              if (updatedMessages.length > 0) {
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                const segments = [...(lastMessage.segments || [])];
                const lastSegment = segments[segments.length - 1];
                const hasUnfinishedCompactionPlaceholder =
                  lastSegment?.type === 'context_compacted' && lastSegment.finished === false;

                if (hasUnfinishedCompactionPlaceholder) {
                  const updatedSegments = [
                    ...segments.slice(0, -1),
                    { ...lastSegment, finished: true } as MessageSegment,
                  ];

                  updatedMessages[updatedMessages.length - 1] = {
                    ...lastMessage,
                    segments: updatedSegments,
                  };
                }
              }
              return updatedMessages;
            });
          }
        };

        const openHandler = () => {
          // Stop pressed while this socket was still CONNECTING: `stopAgent`
          // only acts on an OPEN socket, so without this the turn the user just
          // cancelled would start the moment the connection completes.
          if (userStoppedRef.current) {
            agentWebsocket.close();
            return;
          }
          askAgent(
            agentWebsocket,
            question,
            threadId,
            _portfolioId || undefined,
            _contextualInfo || undefined,
            enableArtifacts,
            floraModel || undefined,
            floraEffort ?? undefined,
            isSidebar,
            documentIds,
            floraApprovalMode,
            floraChatApprovalMode,
          );
        };

        // Safety net for a hard socket drop (worker crash, proxy timeout) where
        // no {type:"error"} / is_done arrives. Only resets the spinner state if
        // this socket is still the active one — a terminal message having nulled
        // the ref means cleanup already ran, so this is a no-op on normal close.
        const socketFailureHandler = () => {
          if (activeWebSocketRef.current !== agentWebsocket) return;
          setIsWaitingForFirstResponse(false);
          setIsActiveQuestion(false);
          setLoadingMessage('Flora is thinking...');
          // A hard drop carries no {type:"error"} payload, so surface the generic
          // fallback — otherwise the spinner just vanishes with no explanation,
          // unlike a backend-emitted error which shows an ErrorBubble. Skipped if
          // the user navigated to another thread (matches the error branch).
          if (threadIdRef.current === originThreadId) {
            setMessages((prevMessages: Message[]) => {
              // Nothing can answer a prompt over a dead socket; retire it rather
              // than leave a card that looks enabled but does nothing.
              const updatedMessages = [...retirePendingPrompts(prevMessages)];
              const errorSegment: MessageSegment = {
                type: 'error',
                message: FLORA_ERROR_FALLBACK_MESSAGE,
              };
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              if (lastMessage && lastMessage.role === 'assistant' && lastMessage.turnId === turnId) {
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  isLoading: false,
                  pendingToolApproval: undefined,
                  segments: [...(lastMessage.segments ?? []), errorSegment],
                };
              } else {
                updatedMessages.push({
                  content: '',
                  role: 'assistant',
                  timestamp: formatToISODate(new Date()) || '',
                  segments: [errorSegment],
                  turnId,
                });
              }
              return updatedMessages;
            });
          }
          activeJobIdRef.current = null;
          try {
            localStorage.removeItem(ACTIVE_JOB_KEY);
          } catch {
            /* storage unavailable */
          }
          activeWebSocketRef.current = null;
        };

        agentWebsocket.addEventListener('message', messageHandler);
        agentWebsocket.addEventListener('open', openHandler);
        agentWebsocket.addEventListener('error', socketFailureHandler);
        agentWebsocket.addEventListener('close', socketFailureHandler);

        return () => {
          agentWebsocket.removeEventListener('message', messageHandler);
          agentWebsocket.removeEventListener('open', openHandler);
          agentWebsocket.removeEventListener('error', socketFailureHandler);
          agentWebsocket.removeEventListener('close', socketFailureHandler);
          if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
          }
          pendingTextRef.current = '';
          if (activeWebSocketRef.current === agentWebsocket) {
            activeWebSocketRef.current = null;
          }
          agentWebsocket.close();
        };
      }
      return undefined;
    },
    [
      orgId,
      useAI,
      threadId,
      _contextualInfo,
      _portfolioId,
      floraVersion,
      setThreadId,
      setChatHistories,
      setMessages,
      setIsActiveQuestion,
      setIsWaitingForFirstResponse,
      setLoadingMessage,
      setTokenUsage,
      enableArtifacts,
      floraModel,
      floraEffort,
      isSidebar,
      documentIds,
      floraApprovalMode,
      floraChatApprovalMode,
      invalidateSlashCommands,
      invalidateOrganizationDocuments,
      trackMemoryFileWrite,
      settlePromptSubmit,
      commitPendingText,
    ],
  );

  /**
   * Fetches chat histories from the AI agent through a websocket connection.
   * When successful, updates the chat histories state with the received data.
   *
   * @returns {Function} Cleanup function to remove event listeners and close websocket connection
   */
  const fetchChatHistories = useCallback(() => {
    if (orgId && useAI) {
      const agentWebsocket = createAgentWebsocket(orgId as string, floraVersion);

      const messageHandler = (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        if (data.type === 'chat_histories') {
          setChatHistories(data.histories);
          seedFloraChatApprovalModes(data.histories);
        }
        agentWebsocket.close();
      };

      const openHandler = () => {
        getChatHistories(agentWebsocket);
      };

      agentWebsocket.addEventListener('message', messageHandler);
      agentWebsocket.addEventListener('open', openHandler);

      return () => {
        agentWebsocket.removeEventListener('message', messageHandler);
        agentWebsocket.removeEventListener('open', openHandler);
        agentWebsocket.close();
      };
    }
    return undefined;
  }, [orgId, useAI, floraVersion, setChatHistories]);

  /**
   * Handles the deletion of a chat history by its ID.
   *
   * @param {string} id - The unique identifier of the chat history to delete
   * @returns {Function} Cleanup function to remove event listeners and close websocket connection
   */
  const handleDeleteHistory = useCallback(
    (id: string) => {
      if (orgId && useAI) {
        const agentWebsocket = createAgentWebsocket(orgId as string, floraVersion);

        const messageHandler = (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          if (data.type === 'delete_success') {
            setChatHistories((prev) => prev.filter((history) => history.id !== id));
            // Compare against the live thread, not the one captured when the
            // delete started: the caller may already have reset (and the user
            // started a new chat), which a second reset would wipe.
            if (threadIdRef.current === id) {
              handleNewChat();
            }
            agentWebsocket.close();
          }
        };

        const openHandler = () => {
          deleteChatHistory(agentWebsocket, id);
        };

        agentWebsocket.addEventListener('message', messageHandler);
        agentWebsocket.addEventListener('open', openHandler);

        return () => {
          agentWebsocket.removeEventListener('message', messageHandler);
          agentWebsocket.removeEventListener('open', openHandler);
          agentWebsocket.close();
        };
      }
      return undefined;
    },
    [orgId, useAI, floraVersion, handleNewChat, setChatHistories],
  );

  /**
   * Handles editing the description of a chat history.
   *
   * @param {string} id - The unique identifier of the chat history to edit
   * @param {string} newDescription - The new description to set for the chat history
   * @returns {Function} Cleanup function to remove event listeners and close websocket connection
   */
  const handleEditHistory = useCallback(
    (id: string, newDescription: string) => {
      if (orgId && useAI) {
        const agentWebsocket = createAgentWebsocket(orgId as string, floraVersion);

        const messageHandler = (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          if (data.type === 'edit_success') {
            setChatHistories((prev) =>
              prev.map((history) => (history.id === id ? { ...history, description: newDescription } : history)),
            );
            agentWebsocket.close();
          }
        };

        const openHandler = () => {
          editChatHistoryDescription(agentWebsocket, id, newDescription);
        };

        agentWebsocket.addEventListener('message', messageHandler);
        agentWebsocket.addEventListener('open', openHandler);

        return () => {
          agentWebsocket.removeEventListener('message', messageHandler);
          agentWebsocket.removeEventListener('open', openHandler);
          agentWebsocket.close();
        };
      }
      return undefined;
    },
    [orgId, useAI, floraVersion, setChatHistories],
  );

  /**
   * Submits answers to agent questions over the active WebSocket connection.
   * Clears the owning message's pendingQuestions, appends a user_answers
   * segment, and sets isLoading. Only the active job's card can submit: a card
   * left over from another job must not resume that job over this socket.
   *
   * @param {string} jobId - The agent job ID
   * @param {Record<string, string | string[]>} answers - Map of question text to selected answer(s)
   */
  const handleAnswerSubmit = useCallback(
    (jobId: string, answers: Record<string, string | string[]>) => {
      const ws = activeWebSocketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
      }
      if (!jobId || jobId !== activeJobIdRef.current) {
        return;
      }
      const parked = parkedPromptRef.current;
      if (parked?.jobId !== jobId || parked.kind !== 'answer') {
        return;
      }
      parkedPromptRef.current = null;
      const answersSegment: PendingPromptSubmit['answersSegment'] = { type: 'user_answers', answers };
      const pending: PendingPromptSubmit = { jobId, kind: 'answer', socket: ws, answersSegment };
      pendingSubmitRef.current = pending;
      submitAnswer(ws, jobId, answers);

      // The resume makes the job live again — restore the reconnect key so a
      // reload during the resumed streaming can pick the run back up (the key
      // was dropped when the question parked), as handleToolApproval does.
      try {
        localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify({ jobId, threadId: threadIdRef.current, orgId }));
      } catch {
        /* storage unavailable */
      }

      setMessages((prevMessages: Message[]) => {
        const index = findPromptMessageIndex(prevMessages, jobId);
        if (index === -1) return prevMessages;
        const message = prevMessages[index];
        // Kept so a refused submit can put the card back (settlePromptSubmit).
        pending.questions = message.pendingQuestions;
        const updatedMessages = [...prevMessages];
        updatedMessages[index] = {
          ...message,
          pendingQuestions: undefined,
          promptRejection: undefined,
          isLoading: true,
          segments: [...(message.segments || []), answersSegment],
        };
        return updatedMessages;
      });
    },
    [setMessages, orgId],
  );

  /**
   * Stops a running agent job by sending a cancel_job message over the active WebSocket.
   * Clears active state and localStorage tracking.
   */
  const stopAgent = useCallback(() => {
    const ws = activeWebSocketRef.current;
    const jobId = activeJobIdRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      if (jobId) {
        // Keep the socket open: the backend answers with `job_cancelled`, and
        // that frame is what appends the "interrupted" divider to a partially
        // streamed answer. Closing here would silently drop it. A late
        // `reconnect_status` is instead ignored via `userStoppedRef` below.
        cancelJobWs(ws, jobId);
      } else {
        // Stop pressed before job_created arrived — close socket to prevent stale events
        ws.close();
        activeWebSocketRef.current = null;
      }
    }
    // Stop is an explicit end to this turn. Any `reconnect_status` still in
    // flight describes the job we just cancelled, and acting on it re-seeds a
    // blank question bubble and navigates into the thread the user just left.
    userStoppedRef.current = true;
    setIsActiveQuestion(false);
    setIsWaitingForFirstResponse(false);
    setLoadingMessage('Flora is thinking...');
    // Retire any pending approval or question card so it stops accepting input
    setMessages(retirePendingPrompts);
    activeJobIdRef.current = null;
    try {
      localStorage.removeItem(ACTIVE_JOB_KEY);
    } catch {
      /* storage unavailable */
    }
  }, [setIsActiveQuestion, setIsWaitingForFirstResponse, setLoadingMessage, setMessages]);

  /**
   * Closes this instance's socket without touching the shared reconnect key,
   * so a surface that unmounts doesn't leave a parked or streaming socket open
   * behind it while the job stays resumable (a remount resumes from the key).
   * Unlike {@link detachActiveJob}, safe under StrictMode's simulated unmount.
   */
  const releaseActiveSocket = useCallback(() => {
    activeWebSocketRef.current?.close();
    activeWebSocketRef.current = null;
    activeJobIdRef.current = null;
    pendingSubmitRef.current = null;
    parkedPromptRef.current = null;
  }, []);

  /**
   * Releases the UI's hold on the running job WITHOUT cancelling it.
   *
   * Leaving a thread is not a request to stop the work: the Celery task keeps
   * running and persists its result (or parks on an approval) for when the
   * user comes back. Only the explicit Stop control cancels.
   */
  const detachActiveJob = useCallback(() => {
    const detachedJobId = activeJobIdRef.current;
    activeWebSocketRef.current?.close();
    activeWebSocketRef.current = null;
    activeJobIdRef.current = null;
    try {
      // The key is shared by every Flora surface (full page and side panel can
      // be mounted together), so only clear it when it records this instance's
      // job — detaching must never erase another surface's resumable job.
      const stored = localStorage.getItem(ACTIVE_JOB_KEY);
      if (stored && detachedJobId && JSON.parse(stored)?.jobId === detachedJobId) {
        localStorage.removeItem(ACTIVE_JOB_KEY);
      }
    } catch {
      /* storage unavailable or invalid JSON */
    }
  }, []);

  /**
   * Reconnects to an existing job by opening a new WebSocket and replaying accumulated content.
   * @param {string} jobId - The ID of the job to reconnect to
   * @param {string} turnId - The reconnected turn's id, stamped on every message this
   *   reconnect creates so the replay pairs with the right question. The caller must
   *   resolve this itself — from the already-derived trailing question when one exists
   *   (see `deriveTurnsFromHistory`), or by minting one and seeding a placeholder
   *   question when it doesn't — rather than have reconnectAgent generate its own:
   *   doing it here made the id (and therefore the pairing) different across
   *   React StrictMode's double-invoke of a mount-only effect.
   * @param {Function} onTerminalWithoutReplay - Called when the job turns out to be
   *   already finished: nothing is replayed because the turn lives in persisted
   *   history, so the caller must release any render claim blocking that history
   * @param {Function} onReplayStarting - Called right before the empty assistant
   *   message is created to receive replayed content, at the same moment `turnId`
   *   is committed to a message. A caller with no persisted question yet (the
   *   mount-time blind resume) must seed its placeholder question here, not
   *   synchronously before calling reconnectAgent — seeding it earlier races the
   *   sibling history-load effect's `setQuestions([])` for a still-threadId-less
   *   chat and loses every time (that effect is declared first and runs in the
   *   same commit), leaving the eventual answer with no question to pair with.
   * @returns {Function} Cleanup function to close the WebSocket
   */
  const reconnectAgent = useCallback(
    (jobId: string, turnId: string, onTerminalWithoutReplay?: () => void, onReplayStarting?: () => void) => {
      if (!orgId || !useAI) return undefined;

      // A resume is deliberate, so the previous Stop no longer applies.
      userStoppedRef.current = false;

      // Cancel any stale pending flush and clear the buffer so leftover text from a
      // prior turn can't bleed into (or be wiped from) this reconnect's message.
      commitPendingText();
      // Set after the flush above, before this reconnect's socket can deliver any
      // frame — see appendStreamingText's turnId param.
      pendingTurnIdRef.current = turnId;

      // Close the socket this reconnect supersedes (a run parked on a card, or
      // an earlier reconnect): a parked job sends no frames, so its stale-socket
      // guard never fires and the socket would otherwise stay open.
      activeWebSocketRef.current?.close();
      const agentWebsocket = createAgentWebsocket(orgId, floraVersion);
      activeWebSocketRef.current = agentWebsocket;
      activeJobIdRef.current = jobId;
      const memoryFileWrites = createMemoryWriteTracker();

      // Capture the thread this reconnect was initiated for (mutable — updated when thread is assigned)
      let originThreadId = threadIdRef.current;

      let receivedTerminalMessage = false;
      // Completed/cancelled turns were persisted to chat history, which the
      // caller has already rendered — replaying them here would double-render
      // the turn. Set from reconnect_status, which precedes the replay frames.
      let contentAlreadyInHistory = false;
      const RECONNECT_TIMEOUT_MS = 15_000;

      const clearActiveState = () => {
        // Only reset shared UI state if this socket is still the active one
        // (avoids clearing state for a newer run that may have started)
        if (activeWebSocketRef.current !== agentWebsocket) return;

        // Close any subagent still marked in_progress. On reconnect the socket
        // can terminate (done, error, close, timeout) before is_subagent_end
        // arrives, which would otherwise leave the section stuck on "Running".
        // Emit a terminal subagent_end so grouping settles the row.
        setMessages((prevMessages: Message[]) => {
          if (prevMessages.length === 0) return prevMessages;
          const lastMessage = prevMessages[prevMessages.length - 1];
          if (!lastMessage.activeSubagents || lastMessage.activeSubagents.length === 0) {
            return prevMessages;
          }
          const endSegments: MessageSegment[] = lastMessage.activeSubagents.map((sa: SubagentInfo) => ({
            type: 'subagent_end',
            subagent: { ...sa, status: 'completed' },
          }));
          const updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = {
            ...lastMessage,
            segments: [...(lastMessage.segments || []), ...endSegments],
            activeSubagents: [],
          };
          return updatedMessages;
        });

        setIsActiveQuestion(false);
        setIsWaitingForFirstResponse(false);
        setLoadingMessage('Flora is thinking...');
        activeJobIdRef.current = null;
        try {
          localStorage.removeItem(ACTIVE_JOB_KEY);
        } catch {
          /* storage unavailable */
        }
        activeWebSocketRef.current = null;
      };

      // Timeout: if no terminal message within 15s, treat reconnect as failed.
      // Armed now to bound the connect, then re-armed on open (see openHandler)
      // so a pre-connect token refresh in createAgentWebsocket doesn't eat
      // into the replay's budget.
      const handleReconnectTimeout = () => {
        if (!receivedTerminalMessage) {
          clearActiveState();
          agentWebsocket.close();
        }
      };
      let timeoutId = setTimeout(handleReconnectTimeout, RECONNECT_TIMEOUT_MS);

      const messageHandler = (event: MessageEvent) => {
        const data = JSON.parse(event.data);

        // Drop events from a superseded websocket — without this, both
        // sockets stay subscribed and every live frame renders twice.
        // Mirrors the identical guard in runAgent.
        if (activeWebSocketRef.current !== agentWebsocket) {
          agentWebsocket.close();
          return;
        }

        // Check if user has navigated to a different thread since this reconnect started
        const isStaleThread = threadIdRef.current !== originThreadId;
        if (settlePromptSubmit(data, agentWebsocket, isStaleThread)) return;

        // Commit any buffered streaming text before handling a non-text frame so it
        // lands ahead of tool/artifact/terminal segments. Text chunks accumulate in
        // the buffer and flush on rAF (see the is_update branch below).
        if (isStaleThread) {
          pendingTextRef.current = '';
        } else if (!isPureTextFrame(data)) {
          commitPendingText();
        }

        // Handle reconnect_status — set thread ID from job
        if (data.type === 'reconnect_status') {
          // The user pressed Stop while this was in flight. Adopting its
          // thread_id now navigates them into the thread they just stopped, and
          // `onReplayStarting` seeds a blank question bubble for a turn that is
          // no longer running. The cancel is already on its way; ignore this.
          if (userStoppedRef.current) return;
          if (data.thread_id) {
            setThreadId(data.thread_id);
            // Update originThreadId so subsequent messages aren't treated as stale
            originThreadId = data.thread_id;
          }
          contentAlreadyInHistory = data.status === 'COMPLETED' || data.status === 'CANCELLED';
          if (contentAlreadyInHistory) {
            onTerminalWithoutReplay?.();
          }
          if (!contentAlreadyInHistory && !isStaleThread) {
            onReplayStarting?.();
            // Create an empty assistant message to receive the replayed
            // content — the still-live turn is not in chat history yet.
            setMessages((prevMessages: Message[]) => [
              ...prevMessages,
              {
                content: '',
                role: 'assistant',
                timestamp: formatToISODate(new Date()) || '',
                segments: [],
                turnId,
              },
            ]);
          }
        }

        // Live chat title update — runs regardless of stale-thread state.
        if (data.type === 'chat_description_updated' && data.thread_id) {
          setChatHistories((prev) =>
            prev.map((history) =>
              history.id === data.thread_id ? { ...history, description: data.description } : history,
            ),
          );
        }

        // Terminal events always run cleanup, even if stale
        if (data.is_done || data.type === 'error' || data.type === 'stalled' || data.type === 'job_cancelled') {
          receivedTerminalMessage = true;
          clearTimeout(timeoutId);
          // A Stop pressed on this reconnect socket: on a direct cancel the
          // backend answers only the cancelling socket (other tabs get no
          // frame), exactly as for a run socket — so, as runAgent does, retire
          // a replayed card and mark the replayed partial answer interrupted.
          // Skipped for a turn already rendered from chat history. Checked
          // before clearActiveState, which releases this socket's ownership.
          if (
            data.type === 'job_cancelled' &&
            !isStaleThread &&
            !contentAlreadyInHistory &&
            activeWebSocketRef.current === agentWebsocket
          ) {
            setMessages(markTurnInterrupted);
          }
          clearActiveState();
          agentWebsocket.close();
          return;
        }

        if (data.type === 'replay_complete') {
          if (!isStaleThread) {
            setIsWaitingForFirstResponse(false);
          }
          clearTimeout(timeoutId);
          return;
        }

        // Track even a replay of a turn already in chat history: its writes
        // may have finished while this surface was detached (New Chat, a
        // thread switch, a reload), when no socket was listening to refresh
        // the caches. Only rendering is skipped below.
        trackMemoryFileWrite(memoryFileWrites, data);

        // Skip all message/UI updates if the thread is stale
        if (isStaleThread) return;

        // Skip replay rendering for a turn that already lives in chat history
        // (terminal frames and cleanup were handled above).
        if (contentAlreadyInHistory) return;

        // Handle replayed and live text updates
        if (data.is_update) {
          if (data.text && data.text.length > 0) {
            setIsWaitingForFirstResponse(false);
            // Buffer + rAF-flush replayed/live text (mirrors runAgent); non-text and
            // terminal frames flush synchronously first (top of this handler).
            const parentContext = data.parent_context || null;
            if (pendingTextRef.current.length > 0 && pendingParentContextRef.current !== parentContext) {
              commitPendingText();
            }
            pendingTextRef.current += data.text;
            pendingParentContextRef.current = parentContext;
            if (!isPureTextFrame(data)) {
              // Frame also carries a terminal/structural payload — commit now so the
              // text lands before that payload is handled in the branches below.
              commitPendingText();
            } else if (rafIdRef.current === null) {
              rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                if (activeWebSocketRef.current !== agentWebsocket || threadIdRef.current !== originThreadId) {
                  pendingTextRef.current = '';
                  return;
                }
                commitPendingText();
              });
            }
          }
        }

        // Live tool-input streaming after reconnect — mirrors runAgent so a
        // still-running job whose tool is mid-write renders progressively
        // instead of staying blank until execution starts.
        if (data.is_tool_input_chunk && typeof data.tool_name === 'string') {
          setIsWaitingForFirstResponse(false);
          const toolName = data.tool_name;
          setMessages((prevMessages: Message[]) =>
            appendToolInputChunk(
              prevMessages,
              {
                tool_name: toolName,
                tool_call_id: typeof data.tool_call_id === 'string' ? data.tool_call_id : null,
                args: typeof data.args === 'string' ? data.args : '',
              },
              () => createToolCallId(toolName),
            ),
          );
        }

        if (data.is_tool_input_end && typeof data.tool_name === 'string') {
          setMessages((prevMessages: Message[]) =>
            finishToolInputStream(prevMessages, {
              tool_name: data.tool_name as string,
              tool_call_id: typeof data.tool_call_id === 'string' ? data.tool_call_id : null,
              tool_input: (data.tool_input as Record<string, unknown> | null | undefined) ?? null,
            }),
          );
        }

        // Handle replayed tool calls
        if (data.is_tool_start) {
          setIsWaitingForFirstResponse(false);
          // tool_start only ever carries `approved`; anything else is dropped so a
          // running call can never show a "blocked" tag.
          const autoMode =
            toAutoModeOutcome(data.auto_mode) === AutoModeOutcome.Approved ? AutoModeOutcome.Approved : undefined;
          const newToolCall: ToolCall = {
            id: createToolCallId(data.tool_name),
            tool_name: data.tool_name,
            tool_input: data.tool_input || {},
            status: 'pending',
            parent_context: data.parent_context || null,
            ...(autoMode ? { auto_mode: autoMode } : {}),
          };
          setMessages((prevMessages: Message[]) => {
            // On a live reconnect the args may have streamed first; promote that
            // call instead of appending a duplicate. Flat replay has no prior
            // streamed call, so this returns null and we create a new one.
            const adopted = adoptStreamedToolCall(prevMessages, {
              tool_name: data.tool_name,
              tool_input: data.tool_input || {},
              parent_context: data.parent_context || null,
              auto_mode: autoMode,
            });
            if (adopted) {
              return adopted;
            }
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              const segments = lastMessage.segments || [];
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                toolCalls: [...(lastMessage.toolCalls || []), newToolCall],
                segments: [...segments, { type: 'tool_call', toolCall: newToolCall }],
              };
            }
            return updatedMessages;
          });
        }

        if (data.is_tool_end) {
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              const pendingIndex = (lastMessage.toolCalls || []).findIndex(
                (tc: ToolCall) => tc.tool_name === data.tool_name && tc.status === 'pending',
              );
              const updatedToolCalls = (lastMessage.toolCalls || []).map((tc: ToolCall, index: number) =>
                index === pendingIndex
                  ? {
                      ...tc,
                      tool_output:
                        typeof data.tool_output === 'string' ? data.tool_output : JSON.stringify(data.tool_output),
                      status: 'completed' as const,
                    }
                  : tc,
              );
              let segmentUpdated = false;
              const updatedSegments = (lastMessage.segments || []).map((segment: MessageSegment) => {
                if (
                  !segmentUpdated &&
                  segment.type === 'tool_call' &&
                  segment.toolCall.tool_name === data.tool_name &&
                  segment.toolCall.status === 'pending'
                ) {
                  segmentUpdated = true;
                  return {
                    ...segment,
                    toolCall: {
                      ...segment.toolCall,
                      tool_output:
                        typeof data.tool_output === 'string' ? data.tool_output : JSON.stringify(data.tool_output),
                      status: 'completed' as const,
                    },
                  };
                }
                return segment;
              });
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                toolCalls: updatedToolCalls,
                segments: updatedSegments,
              };
            }
            return updatedMessages;
          });

          // See the matching block above — match backend tool names.
          if (data.tool_name === 'slash_command_upsert') {
            invalidateSlashCommands();
          }
        }

        // Handle replayed artifacts
        if (data.is_artifact && data.artifact_data) {
          const artifactData = data.artifact_data as ArtifactSpec;
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              const segments = lastMessage.segments || [];
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                artifacts: [...(lastMessage.artifacts || []), artifactData],
                segments: [...segments, { type: 'artifact', artifact: artifactData }],
              };
            }
            return updatedMessages;
          });
        }

        // Replayed or live auto-mode blocks — mirrors runAgent. A blocked call
        // is persisted without output and replays as this frame, not start/end.
        if (data.is_tool_blocked) {
          const blockedFrame = parseToolBlockedFrame(data);
          if (blockedFrame) {
            setIsWaitingForFirstResponse(false);
            setMessages((prevMessages: Message[]) =>
              applyAutoModeBlock(prevMessages, blockedFrame, () => createToolCallId(blockedFrame.tool_name)),
            );
          }
        }

        // Handle live subagent lifecycle events that arrive after the replay
        // completes (the backend replays a mid-run turn's tool calls flat, then
        // streams new subagent events). Mirrors the runAgent handler so a
        // reconnected turn still nests newly-launched subagents.
        if (data.is_subagent_start && data.subagent_name) {
          const subagentInfo: SubagentInfo = {
            id: normalizeSubagentId(data.subagent_id),
            name: data.subagent_name,
            description: data.description,
            status: 'in_progress',
          };
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                segments: [...(lastMessage.segments || []), { type: 'subagent_start', subagent: subagentInfo }],
                activeSubagents: [...(lastMessage.activeSubagents || []), subagentInfo],
              };
            }
            return updatedMessages;
          });
        }

        if (data.is_subagent_end && data.subagent_name) {
          const subagentId = normalizeSubagentId(data.subagent_id);
          const subagentInfo: SubagentInfo = {
            id: subagentId,
            name: data.subagent_name,
            status: data.error ? 'error' : 'completed',
            output: data.output,
            error: data.error,
          };
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              const updatedActiveSubagents = (lastMessage.activeSubagents || []).filter((sa: SubagentInfo) =>
                subagentId ? sa.id !== subagentId : sa.name !== data.subagent_name,
              );
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                segments: [...(lastMessage.segments || []), { type: 'subagent_end', subagent: subagentInfo }],
                activeSubagents: updatedActiveSubagents,
              };
            }
            return updatedMessages;
          });
        }

        // A replayed clarifying-questions prompt: the job is still
        // WAITING_FOR_INPUT, so rebuild the question card exactly as the live
        // runAgent handler does.
        if (data.is_ask_user_questions && data.questions) {
          const questions = data.questions as AskUserQuestion[];
          const jobId = data.job_id as string;
          parkedPromptRef.current = { jobId, kind: 'answer' };
          // Parked job: the active_job_id path owns any future reload (see the
          // matching removal in the live handler).
          try {
            localStorage.removeItem(ACTIVE_JOB_KEY);
          } catch {
            /* storage unavailable */
          }
          setIsWaitingForFirstResponse(false);
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            // No assistant slot means the turn was cleared out from under the
            // replay (e.g. a history refetch). Recreate it rather than drop
            // the prompt — a lost card leaves the thread unanswerable.
            if (updatedMessages.length === 0) {
              updatedMessages.push({
                content: '',
                role: 'assistant',
                timestamp: formatToISODate(new Date()) || '',
                segments: [],
                turnId,
              });
            }
            const lastMessage = updatedMessages[updatedMessages.length - 1];
            const segments = lastMessage.segments || [];
            updatedMessages[updatedMessages.length - 1] = {
              ...lastMessage,
              segments: [...segments, { type: 'ask_user_questions', questions }],
              pendingQuestions: questions,
              pendingJobId: jobId,
              pendingToolApproval: undefined,
              promptRejection: undefined,
              isLoading: false,
            };
            return updatedMessages;
          });
        }

        // The answers the user already gave to a replayed question card. The
        // backend replays them right after the question while the job is
        // still WAITING (e.g. on a later approval) — without this the answered
        // card comes back live.
        if (data.type === 'user_answers' && data.answers && typeof data.answers === 'object') {
          if (parkedPromptRef.current?.kind === 'answer') parkedPromptRef.current = null;
          setMessages((prevMessages: Message[]) => applyReplayedAnswers(prevMessages, data.answers));
        }

        // Handle a replayed HITL approval prompt — the job is still
        // WAITING_FOR_INPUT (the backend only replays this block then), so
        // restore the pending card exactly as the live handler does.
        if (data.is_tool_approval_required) {
          const toolApproval: ToolApproval = {
            actionRequests: data.action_requests ?? [],
            reviewConfigs: data.review_configs ?? [],
            threadId: data.thread_id,
            jobId: data.job_id,
          };
          parkedPromptRef.current = data.job_id ? { jobId: data.job_id, kind: 'approval' } : null;
          if (data.job_id) {
            submittedApprovalJobIdsRef.current.delete(data.job_id);
          }
          // Parked job: the active_job_id path owns any future
          // reload (see the matching removal in the live handler).
          try {
            localStorage.removeItem(ACTIVE_JOB_KEY);
          } catch {
            /* storage unavailable */
          }
          setIsWaitingForFirstResponse(false);
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            // No assistant slot means the turn was cleared out from under the
            // replay (e.g. a history refetch). Recreate it rather than drop
            // the prompt — a lost card leaves the thread unanswerable.
            if (updatedMessages.length === 0) {
              updatedMessages.push({
                content: '',
                role: 'assistant',
                timestamp: formatToISODate(new Date()) || '',
                segments: [],
                turnId,
              });
            }
            const lastMessage = updatedMessages[updatedMessages.length - 1];
            const segments = lastMessage.segments || [];
            updatedMessages[updatedMessages.length - 1] = {
              ...lastMessage,
              segments: [...segments, { type: 'tool_approval', toolApproval }],
              pendingToolApproval: toolApproval,
              pendingQuestions: undefined,
              pendingJobId: undefined,
              promptRejection: undefined,
              isLoading: false,
            };
            return updatedMessages;
          });
        }

        // Handle suggested followups
        if (data.is_suggested_followups && data.suggested_followups) {
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                suggestedFollowups: data.suggested_followups,
              };
            }
            return updatedMessages;
          });
        }

        // Handle token usage
        if (data.is_token_usage && setTokenUsage) {
          setTokenUsage({
            context_used: data.context_used,
            context_limit: data.context_limit,
            percentage: data.percentage,
          });
        }

        // Handle compaction
        if (data.is_compacting) {
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              const segments = lastMessage.segments || [];
              updatedMessages[updatedMessages.length - 1] = {
                ...lastMessage,
                segments: [...segments, { type: 'context_compacted', percentage: 0, finished: false }],
              };
            }
            return updatedMessages;
          });
        }

        if (data.is_compacting_finished) {
          setMessages((prevMessages: Message[]) => {
            const updatedMessages = [...prevMessages];
            if (updatedMessages.length > 0) {
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              const segments = [...(lastMessage.segments || [])];
              const lastSegment = segments[segments.length - 1];
              if (lastSegment?.type === 'context_compacted' && lastSegment.finished === false) {
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  segments: [...segments.slice(0, -1), { ...lastSegment, finished: true } as MessageSegment],
                };
              }
            }
            return updatedMessages;
          });
        }
      };

      const errorHandler = () => {
        clearTimeout(timeoutId);
        clearActiveState();
      };

      const closeHandler = () => {
        clearTimeout(timeoutId);
        if (!receivedTerminalMessage) {
          clearActiveState();
        }
      };

      const openHandler = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(handleReconnectTimeout, RECONNECT_TIMEOUT_MS);
        // Stop pressed while this socket was still CONNECTING: `stopAgent` only
        // acts on an OPEN socket, so resuming here would restart the very job
        // the user cancelled. Cancel it instead, now that we can actually send.
        if (userStoppedRef.current) {
          if (jobId) cancelJobWs(agentWebsocket, jobId);
          agentWebsocket.close();
          return;
        }
        // The assistant slot for replayed content is created on
        // reconnect_status — only a still-live turn gets one; finished turns
        // are already rendered from chat history.
        reconnectToJob(agentWebsocket, jobId);
      };

      agentWebsocket.addEventListener('message', messageHandler);
      agentWebsocket.addEventListener('open', openHandler);
      agentWebsocket.addEventListener('error', errorHandler);
      agentWebsocket.addEventListener('close', closeHandler);

      return () => {
        clearTimeout(timeoutId);
        agentWebsocket.removeEventListener('message', messageHandler);
        agentWebsocket.removeEventListener('open', openHandler);
        agentWebsocket.removeEventListener('error', errorHandler);
        agentWebsocket.removeEventListener('close', closeHandler);
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        pendingTextRef.current = '';
        if (activeWebSocketRef.current === agentWebsocket) {
          activeWebSocketRef.current = null;
        }
        agentWebsocket.close();
      };
    },
    [
      orgId,
      useAI,
      floraVersion,
      setThreadId,
      setChatHistories,
      setMessages,
      setIsActiveQuestion,
      setIsWaitingForFirstResponse,
      setLoadingMessage,
      setTokenUsage,
      invalidateSlashCommands,
      trackMemoryFileWrite,
      settlePromptSubmit,
      commitPendingText,
    ],
  );
  /**
   * Sends a tool approval response (approve / approve_always / reject) over the
   * active WebSocket. Clears the pendingToolApproval from the last message and
   * sets isLoading.
   *
   * @param {ToolApprovalDecision} approvalDecision - The user's decision for the gated tools
   * @param {ToolApproval} toolApproval - The ToolApproval object containing jobId and action details
   */
  const handleToolApproval = useCallback(
    (approvalDecision: ToolApprovalDecision, toolApproval: ToolApproval) => {
      if (submittedApprovalJobIdsRef.current.has(toolApproval.jobId)) {
        return;
      }
      const ws = activeWebSocketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
      }
      if (!toolApproval.jobId || toolApproval.actionRequests.length === 0) {
        return;
      }
      // Only the active job's card can decide: a card left over from another
      // job must not resume that job over this socket.
      if (toolApproval.jobId !== activeJobIdRef.current) {
        return;
      }
      const parked = parkedPromptRef.current;
      if (parked?.jobId !== toolApproval.jobId || parked.kind !== 'approval') {
        return;
      }
      parkedPromptRef.current = null;
      submittedApprovalJobIdsRef.current.add(toolApproval.jobId);
      pendingSubmitRef.current = { jobId: toolApproval.jobId, kind: 'approval', socket: ws, toolApproval };
      const decisions = toolApproval.actionRequests.map(() =>
        approvalDecision === 'reject'
          ? { type: 'reject' as const, message: 'User rejected the action' }
          : { type: approvalDecision },
      );
      sendToolApproval(ws, toolApproval.jobId, decisions);

      // The resume makes the job live again — restore the reconnect key so a
      // reload during the resumed streaming can pick the run back up (the key
      // was dropped when the prompt arrived).
      try {
        localStorage.setItem(
          ACTIVE_JOB_KEY,
          JSON.stringify({ jobId: toolApproval.jobId, threadId: toolApproval.threadId, orgId }),
        );
      } catch {
        /* storage unavailable */
      }

      if (approvalDecision === 'approve_always') {
        // The backend stores an auto-approve override — refetch so the
        // Customize Flora page shows the flipped setting without a reload.
        invalidateUserPreferences();
        invalidateCapabilities();
      }

      const decision =
        approvalDecision === 'approve'
          ? ('approved' as const)
          : approvalDecision === 'approve_always'
            ? ('approved_always' as const)
            : ('rejected' as const);
      setMessages((prevMessages: Message[]) => {
        // The message whose card was clicked, not necessarily the last one.
        const index = findPromptMessageIndex(prevMessages, toolApproval.jobId);
        if (index === -1) return prevMessages;
        const message = prevMessages[index];
        const updatedMessages = [...prevMessages];
        // Stamp the decision on the tool_approval segment so it renders as resolved
        const updatedSegments = (message.segments || []).map((segment) =>
          segment.type === 'tool_approval' && segment.toolApproval.jobId === toolApproval.jobId && !segment.decision
            ? { ...segment, decision }
            : segment,
        );
        // isLoading stays true for every decision: it reflects that a resume is
        // still in flight (is_done clears it). Note this is now purely a display
        // flag — is_start's decision to continue this message vs. push a new one
        // is keyed on turnId, not isLoading; see the messageHandler above.
        updatedMessages[index] = {
          ...message,
          segments: updatedSegments,
          pendingToolApproval: undefined,
          promptRejection: undefined,
          isLoading: true,
        };
        return updatedMessages;
      });
    },
    [setMessages, invalidateUserPreferences, invalidateCapabilities, orgId],
  );

  return {
    runAgent,
    fetchChatHistories,
    handleDeleteHistory,
    handleEditHistory,
    handleAnswerSubmit,
    stopAgent,
    detachActiveJob,
    releaseActiveSocket,
    reconnectAgent,
    handleToolApproval,
  };
};

export { useWebsocketHandlers };
